import React from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import FormControl from "react-bootstrap/FormControl";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Table from "react-bootstrap/Table";
import ToggleButtonGroup from "react-bootstrap/ToggleButtonGroup";
import ToggleButton from "react-bootstrap/ToggleButton";
import FormLabel from "react-bootstrap/FormLabel";
import { useApolloClient, useQuery } from "@apollo/client";
import { gettext } from "i18n";
import {
  GetAlarmBySourceUUID,
  GetAlarmBySourceUUID_alert,
  GetAlarmBySourceUUIDVariables,
} from "utils/__generated__/GetAlarmBySourceUUID";
import {
  GetAlarmDatasourceByUUID,
  GetAlarmDatasourceByUUID_source as Datasource,
  GetAlarmDatasourceByUUIDVariables,
} from "utils/__generated__/GetAlarmDatasourceByUUID";
import {
  GetStationNoDataAlarms,
  GetStationNoDataAlarms_alert,
  GetStationNoDataAlarmsVariables,
} from "utils/__generated__/GetStationNoDataAlarms";
import { GetStationsWithNoDataAlarms } from "utils/__generated__/GetStationsWithNoDataAlarms";
import DefaultTooltip from "../common/DefaultTooltip";
import SessionTimeout from "components/common/SessionTimeout";
import PhoneNumberFormControl from "components/form/PhoneNumberFormControl";
import { useModalContext } from "state/modal";
import { useAuth } from "state/auth";
import apiClient from "../../api";
import { toast } from "react-toastify";
import {
  GET_ALARM_DATASOURCE_BY_UUID,
  GET_ALARM_BY_SOURCE_UUID,
  GET_ALL_STATIONS_FOR_ALARMS,
  GET_STATION_DATASOURCES_FOR_ALARMS,
  GET_STATION_NO_DATA_ALARMS,
  GET_STATIONS_WITH_NO_DATA_ALARMS,
  INTERVAL_UNITS,
  ALARM_LEVELS,
  ALARM_LEVEL_COLORS,
  DEFAULT_ALARM_FREQUENCIES,
  DEFAULT_NO_DATA_PERIODS,
  DEFAULT_NO_DATA_MINUTES,
  VALUE_ALARM_KIND,
  STATION_NO_DATA_ALARM_KIND,
  isStationNoDataAlarm,
  isNoDataAlarmEligibleStation,
} from "utils/alarms";
import "./AddNewAlarmForm.scss";

const ToastMessage = ({
  messageHeader,
  message,
}: {
  messageHeader: string;
  message: string;
}) => {
  return (
    <div>
      <p>
        {messageHeader}
        {message ? ":" : ""}
      </p>
      {message && <p>{message}</p>}
    </div>
  );
};

const getRecipientNotificationMessage = (recipientData: {
  email_recipient?: string | null;
  sms_recipient?: string | null;
}): string => {
  const hasEmail = !!(
    recipientData.email_recipient && recipientData.email_recipient.trim()
  );
  const hasSms = !!(
    recipientData.sms_recipient && recipientData.sms_recipient.trim()
  );

  if (hasEmail && hasSms) {
    return "A test email was sent to the email address and a test SMS was sent to the phone number. Please check your spam folder if you don't see the email in your inbox in a few minutes.";
  } else if (hasEmail) {
    return "A test email was sent to the email address. Please check your spam folder if you don't see the email in your inbox in a few minutes.";
  } else if (hasSms) {
    return "A test SMS was sent to the phone number.";
  }

  return "";
};

const AddNewAlarmForm = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.admin;
  const {
    setActiveModal,
    selectedDataSource,
    setSelectedDataSource,
    modalPayload,
    setModalPayload,
  } = useModalContext();
  const [alarmValues, setAlarmValues] = React.useState<Record<
    string,
    any
  > | null>(null);
  const [modalDisableSave, setModalDisableSave] = React.useState<boolean>(true);
  const apolloClient = useApolloClient();

  // modalPayload contains the alarm being edited (if any)
  const editingAlarm = modalPayload as GetAlarmBySourceUUID_alert | null;
  const editingNoDataAlarm = isStationNoDataAlarm(modalPayload)
    ? (modalPayload as GetStationNoDataAlarms_alert)
    : null;

  const [selectedStationId, setSelectedStationId] = React.useState<
    number | null
  >(editingNoDataAlarm?.station_id ?? null);
  const [alarmKind, setAlarmKind] = React.useState<string>(
    editingNoDataAlarm ? STATION_NO_DATA_ALARM_KIND : VALUE_ALARM_KIND
  );

  // Query all stations
  const { data: stationsData } = useQuery(GET_ALL_STATIONS_FOR_ALARMS);

  // Query data sources for selected station
  const { data: dataSourcesData } = useQuery(
    GET_STATION_DATASOURCES_FOR_ALARMS,
    {
      variables: { stationId: selectedStationId! },
      skip: !selectedStationId,
    }
  );

  const sourceUUID = selectedDataSource?.source_uuid;
  const hasDataSource = !!sourceUUID;
  // A station not sending data alarm watches the whole station instead of a data source
  const isNoDataMode = !hasDataSource && alarmKind === STATION_NO_DATA_ALARM_KIND;

  const { data: sourceData, error: sourceError } = useQuery<
    GetAlarmDatasourceByUUID,
    GetAlarmDatasourceByUUIDVariables
  >(GET_ALARM_DATASOURCE_BY_UUID, {
    variables: { sourceUUID },
    skip: !hasDataSource,
  });

  const { data: alarmData, refetch: alarmRefetch } = useQuery<
    GetAlarmBySourceUUID,
    GetAlarmBySourceUUIDVariables
  >(GET_ALARM_BY_SOURCE_UUID, {
    variables: { sourceUUID },
    skip: !hasDataSource,
  });

  const { data: noDataAlarmData, refetch: noDataAlarmRefetch } = useQuery<
    GetStationNoDataAlarms,
    GetStationNoDataAlarmsVariables
  >(GET_STATION_NO_DATA_ALARMS, {
    variables: { stationId: selectedStationId! },
    skip: !isNoDataMode || !selectedStationId,
  });

  // Alarms may have been added or deleted elsewhere since the last visit, so do not trust the cache alone
  const { data: stationsWithNoDataAlarmsData } =
    useQuery<GetStationsWithNoDataAlarms>(GET_STATIONS_WITH_NO_DATA_ALARMS, {
      fetchPolicy: "cache-and-network",
      skip: !isNoDataMode || !!editingAlarm,
    });
  const stationsWithNoDataAlarm = React.useMemo(
    () =>
      new Set(
        (stationsWithNoDataAlarmsData?.alert || []).map(
          (alarm) => alarm.station_id
        )
      ),
    [stationsWithNoDataAlarmsData]
  );

  const currentNoDataAlarm = React.useMemo(() => {
    if (!editingNoDataAlarm || !noDataAlarmData) return editingNoDataAlarm;
    return (
      noDataAlarmData.alert.find(
        (alarm) => alarm.id === editingNoDataAlarm.id
      ) || editingNoDataAlarm
    );
  }, [editingNoDataAlarm, noDataAlarmData]);

  // Get the fresh alarm data from the query
  // If editing, find the matching alarm from the query results
  // This ensures recipients list is always up-to-date after refetch
  const currentAlarm = React.useMemo(() => {
    if (!editingAlarm || !alarmData) return editingAlarm;

    const alerts = alarmData.alert || [];
    const matchingAlarm = alerts.find(
      (alarm: any) => alarm.id === editingAlarm.id
    );

    // Return the fresh alarm data if found, otherwise fall back to editingAlarm
    return matchingAlarm || editingAlarm;
  }, [editingAlarm, alarmData]);

  if (sourceError) {
    console.error("AddNewAlarmForm GQL error: ", sourceError);
    return <SessionTimeout />;
  }

  // Hide unrelated source types
  const allowedDatasourceTypes = ["channel", "formula", "manual_point_set"];
  const datasource: Datasource | null | undefined = sourceData?.source;

  const resource_key = `${selectedDataSource?.type}-${selectedDataSource?.source_id}`;
  // Identifies the alarm's organization for the API permission check
  const requestParams: Record<string, any> = isNoDataMode
    ? { stationId: selectedStationId }
    : { resource_key };
  const refetchCurrentAlarm = isNoDataMode ? noDataAlarmRefetch : alarmRefetch;
  const selectedStation = stationsData?.station.find(
    (station: any) => station.id === selectedStationId
  );

  const onHide = () => {
    setAlarmValues(null);
    setModalDisableSave(true);
    if (isNoDataMode && editingAlarm) {
      // The alarm or its recipients may have changed without Save, e.g. when the first recipient created the alarm
      apolloClient.refetchQueries({ include: ["GetAllStationAlarmsQuery"] });
    }
    setActiveModal(null);
  };

  const handleStationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stationId = parseInt(e.target.value);
    setSelectedStationId(stationId || null);
    setSelectedDataSource(null);
  };

  const handleAlarmKindChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAlarmKind(e.target.value);
    setSelectedStationId(null);
    setSelectedDataSource(null);
    setAlarmValues(null);
    setModalDisableSave(true);
  };

  const handleDataSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (!value) {
      setSelectedDataSource(null);
      return;
    }

    const [type, sourceId] = value.split("-");
    const dataSource = dataSourcesData?.station[0]?.datasources.find(
      (ds: any) => ds.type === type && ds.source_id === parseInt(sourceId)
    );

    if (dataSource) {
      // Update the global selected data source in modal context
      setSelectedDataSource(dataSource);
    }
  };

  const handleSaveComplete = () => {
    setAlarmValues(null);
    setModalDisableSave(true);
    if (isNoDataMode) {
      // Station-level alarms are not listed per data source, so refresh the alarm list and close. The station
      // picker's list is not refetched here: it reloads itself when the form opens, and is skipped while editing
      apolloClient.refetchQueries({ include: ["GetAllStationAlarmsQuery"] });
      setActiveModal(null);
      return;
    }
    // After saving, return to the configure alarms view
    setActiveModal("configureAlarms");
  };

  const saveAlarm = (
    shouldNavigateAway: boolean = true
  ): Promise<number | null> => {
    return new Promise((resolve, reject) => {
      if (alarmValues) {
        const values = { ...alarmValues };
        const alarmParams = isNoDataMode
          ? { kind: STATION_NO_DATA_ALARM_KIND, ...requestParams }
          : { resource_key, source_uuid: selectedDataSource?.source_uuid };
        const requestPath = !editingAlarm
          ? "/organization-admin/alert/create"
          : "/organization-admin/alert/edit";
        const successMessage = !editingAlarm
          ? "Alarm created successfully"
          : "Alarm updated successfully";
        const errorMessage = !editingAlarm
          ? "Error creating alarm"
          : "Error updating alarm";

        apiClient
          .request(requestPath, {
            method: "POST",
            data: {
              ...alarmParams,
              alert_id: editingAlarm?.id,
              ...values,
            },
          })
          .then((response: any) => {
            toast.success(
              <ToastMessage messageHeader={successMessage} message="" />
            );
            refetchCurrentAlarm()
              .then(() => {
                if (shouldNavigateAway) {
                  handleSaveComplete();
                }
                // Return the alert_id from the response (for new alarms) or existing id
                const alertId = response.alert_id || editingAlarm?.id || null;
                resolve(alertId);
              })
              .catch((e) => {
                toast.error(
                  <ToastMessage
                    messageHeader="Error when re-fetching the latest update of alarm"
                    message={e.message.toString()}
                  />
                );
                reject(e);
              });
          })
          .catch((e) => {
            toast.error(
              <ToastMessage
                messageHeader={errorMessage}
                message={e.message.toString()}
              />
            );
            reject(e);
          });
      } else {
        reject(new Error("No alarm values"));
      }
    });
  };

  const deleteAlarm = () => {
    const alert_id = editingAlarm?.id;
    const requestPath = "/organization-admin/alert/delete";
    const successMessage = "Alarm deleted successfully";
    const errorMessage = "Error deleting alarm";

    if (
      window.confirm(gettext("Are you sure you want to delete this alarm?"))
    ) {
      apiClient
        .request(requestPath, {
          method: "POST",
          data: {
            ...requestParams,
            alert_id: alert_id,
          },
        })
        .then(() => {
          toast.success(
            <ToastMessage messageHeader={successMessage} message="" />
          );
          refetchCurrentAlarm()
            .then(() => {
              handleSaveComplete();
            })
            .catch((e) => {
              toast.error(
                <ToastMessage
                  messageHeader="Error when re-fetching the latest update of alarm"
                  message={e.message.toString()}
                />
              );
            });
        })
        .catch((e) => {
          toast.error(
            <ToastMessage
              messageHeader={errorMessage}
              message={e.message.toString()}
            />
          );
        });
    }
  };

  return (
    <Modal
      show={true}
      onHide={onHide}
      size="lg"
      aria-labelledby="add-alarm-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="add-alarm-modal">
          {editingAlarm ? gettext("Edit Alarm") : gettext("Add New Alarm")}
          {isNoDataMode && selectedStation && <>: {selectedStation.name}</>}
          {hasDataSource && datasource && (
            <>
              : {datasource.name}
              {datasource.unit?.symbol && ` [${datasource.unit.symbol}]`}
            </>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!hasDataSource && !editingAlarm && (
          <Form.Group as={Row} className="mb-3">
            <FormLabel column sm={3}>
              {gettext("Alarm Type")}{" "}
              <DefaultTooltip
                popHeader={gettext("Alarm Type")}
                popBody={
                  <>
                    <p>
                      {gettext(
                        "A parameter alarm watches the values of a single parameter. A station not sending data alarm is triggered when the station has not received any data for the selected period."
                      )}
                    </p>
                  </>
                }
              />
            </FormLabel>
            <Col sm={9}>
              <Form.Select value={alarmKind} onChange={handleAlarmKindChange}>
                <option value={VALUE_ALARM_KIND}>
                  {gettext("Parameter alarm")}
                </option>
                <option value={STATION_NO_DATA_ALARM_KIND}>
                  {gettext("Station not sending data")}
                </option>
              </Form.Select>
            </Col>
          </Form.Group>
        )}

        {isNoDataMode && (
          <>
            {!editingAlarm && (
              <Form.Group as={Row} className="mb-3">
                <FormLabel column sm={3}>
                  {gettext("Station")}
                </FormLabel>
                <Col sm={9}>
                  <Form.Select
                    value={selectedStationId || ""}
                    onChange={handleStationChange}
                  >
                    <option value="">{gettext("Select a station...")}</option>
                    {stationsData?.station
                      .filter(isNoDataAlarmEligibleStation)
                      .map((station: any) => {
                        const hasAlarm = stationsWithNoDataAlarm.has(
                          station.id
                        );
                        return (
                          <option
                            key={station.id}
                            value={station.id}
                            // Global admins may still add an internal alarm next to the organization's one
                            disabled={hasAlarm && !isAdmin}
                          >
                            {`${station.name} (${station.identifier})`}
                            {hasAlarm && ` - ${gettext("alarm already set")}`}
                          </option>
                        );
                      })}
                  </Form.Select>
                </Col>
              </Form.Group>
            )}

            {selectedStationId && (
              <>
                <StationNoDataAlarmInputs
                  alarmData={currentNoDataAlarm}
                  alarmValues={alarmValues}
                  setAlarmValues={setAlarmValues}
                  setModalDisableSave={setModalDisableSave}
                  isAdmin={isAdmin}
                />

                <AlarmRecipients
                  alarmData={currentNoDataAlarm}
                  requestParams={requestParams}
                  refetchAlarm={noDataAlarmRefetch}
                  saveAlarm={saveAlarm}
                  modalDisableSave={modalDisableSave}
                  setModalPayload={setModalPayload}
                />
              </>
            )}
          </>
        )}

        {!hasDataSource && !isNoDataMode && (
          <>
            <h4>{gettext("Select Data Source")}</h4>
            <Form.Group as={Row} className="mb-3">
              <FormLabel column sm={3}>
                {gettext("Station")}
              </FormLabel>
              <Col sm={9}>
                <Form.Select
                  value={selectedStationId || ""}
                  onChange={handleStationChange}
                >
                  <option value="">{gettext("Select a station...")}</option>
                  {stationsData?.station
                    .filter((station: any) => station.origin !== "fmi")
                    .map((station: any) => (
                      <option key={station.id} value={station.id}>
                        {station.name} ({station.identifier})
                      </option>
                    ))}
                </Form.Select>
              </Col>
            </Form.Group>

            {selectedStationId && (
              <Form.Group as={Row} className="mb-3">
                <FormLabel column sm={3}>
                  {gettext("Parameter")}
                </FormLabel>
                <Col sm={9}>
                  <Form.Select
                    value={
                      selectedDataSource
                        ? `${selectedDataSource.type}-${selectedDataSource.source_id}`
                        : ""
                    }
                    onChange={handleDataSourceChange}
                  >
                    <option value="">{gettext("Select a parameter...")}</option>
                    {dataSourcesData?.station[0]?.datasources
                      .filter((ds: any) =>
                        allowedDatasourceTypes.includes(ds.type)
                      )
                      .map((ds: any) => (
                        <option
                          key={`${ds.type}-${ds.source_id}`}
                          value={`${ds.type}-${ds.source_id}`}
                        >
                          {ds.name} {ds.unit ? `(${ds.unit.symbol})` : ""}
                        </option>
                      ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            )}

            {selectedDataSource && !editingAlarm && (
              <div className="alert alert-success mt-3">
                <p>
                  {gettext(
                    "Data source selected. You can now configure the alarm below."
                  )}
                </p>
              </div>
            )}
          </>
        )}

        {hasDataSource && (
          <>
            <AlarmInputs
              alarmData={currentAlarm}
              alarmValues={alarmValues}
              setAlarmValues={setAlarmValues}
              setModalDisableSave={setModalDisableSave}
              isAdmin={isAdmin}
            />

            <AlarmRecipients
              alarmData={currentAlarm}
              requestParams={requestParams}
              refetchAlarm={alarmRefetch}
              saveAlarm={saveAlarm}
              modalDisableSave={modalDisableSave}
              setModalPayload={setModalPayload}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {editingAlarm && (
          <Button variant="danger" onClick={deleteAlarm} className="me-auto">
            {gettext("Delete")}
          </Button>
        )}
        <Button variant="secondary" onClick={onHide}>
          {gettext("Cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={() => saveAlarm().catch(console.error)}
          disabled={
            (isNoDataMode ? !selectedStationId : !hasDataSource) ||
            modalDisableSave
          }
        >
          {gettext("Save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface AlarmLevelFieldProps {
  value: string | undefined;
  onChange: (value: string) => void;
}

const AlarmLevelField = ({ value, onChange }: AlarmLevelFieldProps) => (
  <Form.Group as={Row} className="mb-3">
    <FormLabel column sm={3}>
      {gettext("Alarm Level")}{" "}
      <DefaultTooltip
        popHeader={gettext("Alarm Level")}
        popBody={
          <>
            <p>
              {gettext(
                "Different alarm levels (notice, warning, alert) are used to distinguish between critical and non-critical alarms. Selected level indicates the color of the alarm annotation in graphs."
              )}
            </p>
          </>
        }
      />
    </FormLabel>
    <Col sm={9}>
      <ToggleButtonGroup
        type="radio"
        name="level"
        value={value || "notice"}
        onChange={onChange}
      >
        {ALARM_LEVELS.map((level) => (
          <ToggleButton
            key={level}
            id={`level-${level}`}
            value={level}
            variant="outline-primary"
            style={{
              backgroundColor:
                value === level ? ALARM_LEVEL_COLORS[level] : "transparent",
              color: value === level ? "white" : "inherit",
              borderColor: ALARM_LEVEL_COLORS[level],
            }}
          >
            {gettext(level)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Col>
  </Form.Group>
);

interface AlertFrequencyFieldProps {
  value: number | string | undefined;
  onChange: (e: any) => void;
  tooltip?: string;
}

const AlertFrequencyField = ({
  value,
  onChange,
  tooltip,
}: AlertFrequencyFieldProps) => (
  <Form.Group as={Row} className="mb-3">
    <FormLabel column sm={3}>
      {gettext("Alert Frequency")}{" "}
      <DefaultTooltip
        popHeader={gettext("Alert Frequency")}
        popBody={
          <>
            <p>
              {tooltip ||
                gettext(
                  "How often to send alerts when alarm conditions are met"
                )}
            </p>
          </>
        }
      />
    </FormLabel>
    <Col sm={9}>
      <Form.Select value={value || 1440} onChange={onChange}>
        {DEFAULT_ALARM_FREQUENCIES.map(([label, minutes]) => (
          <option key={minutes} value={minutes}>
            {label}
          </option>
        ))}
      </Form.Select>
    </Col>
  </Form.Group>
);

interface InternalFieldProps {
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const InternalField = ({ checked, onChange }: InternalFieldProps) => (
  <Form.Group as={Row} className="mb-3">
    <FormLabel column sm={3}>
      {gettext("Internal")}{" "}
      <DefaultTooltip
        popHeader={gettext("Internal")}
        popBody={
          <>
            <p>
              {gettext(
                "Internal alarms are only visible to system administrators"
              )}
            </p>
          </>
        }
      />
    </FormLabel>
    <Col sm={9}>
      <Form.Check
        type="switch"
        id="custom-switch-internal"
        label={checked ? gettext("Enabled") : gettext("Disabled")}
        checked={checked}
        onChange={onChange}
      />
    </Col>
  </Form.Group>
);

interface AlarmInputsProps {
  alarmData: GetAlarmBySourceUUID_alert | null;
  alarmValues: Record<string, any> | null;
  setAlarmValues: (values: Record<string, any> | null) => void;
  setModalDisableSave: (disabled: boolean) => void;
  isAdmin: boolean;
}

const AlarmInputs = ({
  alarmData,
  alarmValues,
  setAlarmValues,
  setModalDisableSave,
  isAdmin,
}: AlarmInputsProps) => {
  const initialAlarmOptions = alarmData?.alert_options?.[0] || {
    ignore_spikes: false,
    trend_alarm: false,
    trend_alarm_interval: 24,
    trend_alarm_interval_unit: "hours",
    change_alarm: false,
    change_alarm_threshold: 10,
    change_alarm_comparison: "last_value",
    change_alarm_interval: 60,
    change_alarm_interval_unit: "minutes",
  };

  const [alarmOptions, setAlarmOptions] = React.useState(initialAlarmOptions);
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  React.useEffect(() => {
    if (alarmData) {
      const initialValues: Record<string, any> = {
        level: alarmData.level || "notice",
        lower_limit: alarmData.lower_limit ?? "",
        upper_limit: alarmData.upper_limit ?? "",
        alert_frequency_minutes: alarmData.alert_frequency_minutes || 1440,
        alert_timeframe_minutes: alarmData.alert_timeframe_minutes || 15,
        active: alarmData.active ?? true,
        internal: (alarmData as any).internal ?? false,
      };

      if (alarmData.alert_options?.[0]) {
        // Store alert_options as a nested object
        initialValues.alert_options = {
          ignore_spikes: alarmData.alert_options[0].ignore_spikes ?? false,
          trend_alarm: alarmData.alert_options[0].trend_alarm ?? false,
          trend_alarm_interval:
            alarmData.alert_options[0].trend_alarm_interval ?? 24,
          trend_alarm_interval_unit:
            alarmData.alert_options[0].trend_alarm_interval_unit || "hours",
          change_alarm: alarmData.alert_options[0].change_alarm ?? false,
          change_alarm_threshold:
            alarmData.alert_options[0].change_alarm_threshold ?? 10,
          change_alarm_comparison:
            alarmData.alert_options[0].change_alarm_comparison || "last_value",
          change_alarm_interval:
            alarmData.alert_options[0].change_alarm_interval ?? 60,
          change_alarm_interval_unit:
            alarmData.alert_options[0].change_alarm_interval_unit || "minutes",
        };
      } else {
        // Default alert_options for alarms without options
        initialValues.alert_options = {
          ignore_spikes: false,
          trend_alarm: false,
          trend_alarm_interval: 24,
          trend_alarm_interval_unit: "hours",
          change_alarm: false,
          change_alarm_threshold: 10,
          change_alarm_comparison: "last_value",
          change_alarm_interval: 60,
          change_alarm_interval_unit: "minutes",
        };
      }

      setAlarmValues(initialValues);
      setModalDisableSave(false);
    } else {
      // Initialize defaults for new alarms
      const defaultValues: Record<string, any> = {
        level: "notice",
        lower_limit: "",
        upper_limit: "",
        alert_frequency_minutes: 1440,
        alert_timeframe_minutes: 15,
        active: true,
        internal: false,
        alert_options: {
          ignore_spikes: false,
          trend_alarm: false,
          trend_alarm_interval: 24,
          trend_alarm_interval_unit: "hours",
          change_alarm: false,
          change_alarm_threshold: 10,
          change_alarm_comparison: "last_value",
          change_alarm_interval: 60,
          change_alarm_interval_unit: "minutes",
        },
      };
      setAlarmValues(defaultValues);
      setModalDisableSave(true);
    }
  }, [alarmData, setAlarmValues, setModalDisableSave]);

  const handleInputChange = (key: string) => (e: any) => {
    const value = e.target.value;
    const newValues = {
      ...(alarmValues || {}),
      [key]: value,
    };
    setAlarmValues(newValues);

    const hasValidValues =
      (newValues.lower_limit !== "" && newValues.lower_limit !== null) ||
      (newValues.upper_limit !== "" && newValues.upper_limit !== null);

    setModalDisableSave(!hasValidValues);
  };

  const handleLevelChange = (value: string) => {
    const newValues = {
      ...(alarmValues || {}),
      level: value,
    };
    setAlarmValues(newValues);
  };

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValues = {
      ...(alarmValues || {}),
      active: e.target.checked,
    };
    setAlarmValues(newValues);
  };

  const handleInternalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValues = {
      ...(alarmValues || {}),
      internal: e.target.checked,
    };
    setAlarmValues(newValues);
  };

  const handleAdvancedOptionChange = (key: string) => (e: any) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const newOptions = {
      ...alarmOptions,
      [key]: value,
    };
    setAlarmOptions(newOptions);

    // Store alert options as a nested object
    const newValues = {
      ...(alarmValues || {}),
      alert_options: {
        ...(alarmValues?.alert_options || {}),
        [key]: value,
      },
    };
    setAlarmValues(newValues);
  };

  return (
    <>
      <h4>{gettext("Alarm Configuration")}</h4>

      <Form.Group as={Row} className="mb-3">
        <FormLabel column sm={3}>
          {gettext("Upper Limit")}
        </FormLabel>
        <Col sm={9}>
          <FormControl
            type="number"
            step="any"
            value={alarmValues?.upper_limit ?? ""}
            onChange={handleInputChange("upper_limit")}
            placeholder={gettext("(optional)")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-3">
        <FormLabel column sm={3}>
          {gettext("Lower Limit")}
        </FormLabel>
        <Col sm={9}>
          <FormControl
            type="number"
            step="any"
            value={alarmValues?.lower_limit ?? ""}
            onChange={handleInputChange("lower_limit")}
            placeholder={gettext("(optional)")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-3">
        <FormLabel column sm={3}>
          {gettext("Active")}
        </FormLabel>
        <Col sm={9}>
          <Form.Check
            type="switch"
            id="custom-switch-active"
            label={
              alarmValues?.active ?? true
                ? gettext("Enabled")
                : gettext("Disabled")
            }
            checked={alarmValues?.active ?? true}
            onChange={handleActiveChange}
          />
        </Col>
      </Form.Group>

      <div className="mb-3">
        <Button
          variant="link"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="p-0"
        >
          {showAdvanced
            ? gettext("Hide Advanced Options")
            : gettext("Show Advanced Options")}
        </Button>
      </div>

      {showAdvanced && (
        <>
          <h5>{gettext("Advanced Options")}</h5>

          <AlarmLevelField
            value={alarmValues?.level}
            onChange={handleLevelChange}
          />

          <AlertFrequencyField
            value={alarmValues?.alert_frequency_minutes}
            onChange={handleInputChange("alert_frequency_minutes")}
          />

          {isAdmin && (
            <InternalField
              checked={!!alarmValues?.internal}
              onChange={handleInternalChange}
            />
          )}

          <Form.Group as={Row} className="mb-3">
            <FormLabel column sm={3}>
              {gettext("Ignore Spikes")}{" "}
              <DefaultTooltip
                popHeader={gettext("Ignore Spikes")}
                popBody={
                  <>
                    <p>
                      {gettext(
                        "Ignore individual values, that cross the set limit. If this setting is activated, alarm will not go off if only a single value is passing the limit (such as a single disturbance in measurement signal). Alarm is activated only once there is two consecutive results passing the limit."
                      )}
                    </p>
                  </>
                }
              />
            </FormLabel>
            <Col sm={9}>
              <Form.Check
                type="switch"
                id="custom-switch-ignore-spikes"
                label={
                  alarmOptions.ignore_spikes || false
                    ? gettext("Enabled")
                    : gettext("Disabled")
                }
                checked={alarmOptions.ignore_spikes || false}
                onChange={handleAdvancedOptionChange("ignore_spikes")}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <FormLabel column sm={3}>
              {gettext("Average Alarm")}{" "}
              <DefaultTooltip
                popHeader={gettext("Average Alarm")}
                popBody={
                  <>
                    <p>
                      {gettext(
                        "Average alarm enables you to set an alarm only when the average for a certain time period passes the set limit. For example, when the 24-hour average of measured discharge passes the limit set above. Set here the timeframe you want to use for average calculation: for example 24 hours or 14 days."
                      )}
                    </p>
                  </>
                }
              />
            </FormLabel>
            <Col sm={9}>
              <Form.Check
                type="switch"
                id="custom-switch-trend-alarm"
                label={
                  alarmOptions.trend_alarm || false
                    ? gettext("Enabled")
                    : gettext("Disabled")
                }
                checked={alarmOptions.trend_alarm || false}
                onChange={handleAdvancedOptionChange("trend_alarm")}
              />
            </Col>
          </Form.Group>

          {alarmOptions.trend_alarm && (
            <>
              <Form.Group as={Row} className="mb-3">
                <FormLabel column sm={3}>
                  {gettext("Timeframe")}
                </FormLabel>
                <Col sm={6}>
                  <FormControl
                    type="number"
                    value={alarmOptions.trend_alarm_interval ?? 24}
                    onChange={handleAdvancedOptionChange(
                      "trend_alarm_interval"
                    )}
                    placeholder={gettext("24")}
                  />
                </Col>
                <Col sm={3}>
                  <Form.Select
                    value={alarmOptions.trend_alarm_interval_unit || "hours"}
                    onChange={handleAdvancedOptionChange(
                      "trend_alarm_interval_unit"
                    )}
                  >
                    {INTERVAL_UNITS.map((unit) => (
                      <option key={unit} value={unit}>
                        {gettext(unit)}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </>
          )}

          <Form.Group as={Row} className="mb-3">
            <FormLabel column sm={3}>
              {gettext("Trend Alarm")}{" "}
              <DefaultTooltip
                popHeader={gettext("Trend Alarm")}
                popBody={
                  <>
                    <p>
                      {gettext(
                        "Trend alarm triggers when the measurement changes by more than a specified percentage compared with a reference value, within a chosen time period."
                      )}
                    </p>
                  </>
                }
              />
            </FormLabel>
            <Col sm={9}>
              <Form.Check
                type="switch"
                id="custom-switch-change-alarm"
                label={
                  alarmOptions.change_alarm || false
                    ? gettext("Enabled")
                    : gettext("Disabled")
                }
                checked={alarmOptions.change_alarm || false}
                onChange={handleAdvancedOptionChange("change_alarm")}
              />
            </Col>
          </Form.Group>

          {alarmOptions.change_alarm && (
            <>
              <div className="trend-alarm-section">
                <Form.Group as={Row} className="mb-3">
                  <FormLabel column sm={3}>
                    {gettext("Detected change in measurement differs")}
                  </FormLabel>
                  <Col sm={9}>
                    <div className="d-flex align-items-center gap-2">
                      <FormControl
                        type="number"
                        step="any"
                        value={alarmOptions.change_alarm_threshold ?? 10}
                        onChange={handleAdvancedOptionChange(
                          "change_alarm_threshold"
                        )}
                        placeholder="10"
                        style={{ maxWidth: "120px" }}
                      />
                      <span>%</span>
                    </div>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-3">
                  <FormLabel column sm={3}>
                    {gettext("from the")}
                  </FormLabel>
                  <Col sm={9}>
                    <div className="trend-alarm-from-the">
                      <Form.Select
                        value={
                          alarmOptions.change_alarm_comparison || "last_value"
                        }
                        onChange={handleAdvancedOptionChange(
                          "change_alarm_comparison"
                        )}
                      >
                        <option value="last_value">
                          {gettext("latest values")}
                        </option>
                        <option value="average_value">
                          {gettext("average value")}
                        </option>
                      </Form.Select>
                      <span className="separator">{gettext("/")}</span>
                      <FormControl
                        type="number"
                        value={alarmOptions.change_alarm_interval ?? 60}
                        onChange={handleAdvancedOptionChange(
                          "change_alarm_interval"
                        )}
                        placeholder="60"
                      />
                      <Form.Select
                        value={
                          alarmOptions.change_alarm_interval_unit || "minutes"
                        }
                        onChange={handleAdvancedOptionChange(
                          "change_alarm_interval_unit"
                        )}
                      >
                        {INTERVAL_UNITS.map((unit) => (
                          <option key={unit} value={unit}>
                            {gettext(unit)}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </Col>
                </Form.Group>

                <div className="alert alert-info">
                  <small>
                    {gettext(
                      "Detected change in measurement differs by at least %1%% from the %2 within %3 %4",
                      alarmOptions.change_alarm_threshold,
                      gettext(
                        (alarmOptions.change_alarm_comparison ||
                          "last_value") === "last_value"
                          ? "latest values"
                          : "average value"
                      ),
                      alarmOptions.change_alarm_interval,
                      gettext(
                        alarmOptions.change_alarm_interval_unit || "minutes"
                      )
                    )}
                  </small>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

interface StationNoDataAlarmInputsProps {
  alarmData: GetStationNoDataAlarms_alert | null;
  alarmValues: Record<string, any> | null;
  setAlarmValues: (values: Record<string, any> | null) => void;
  setModalDisableSave: (disabled: boolean) => void;
  isAdmin: boolean;
}

const StationNoDataAlarmInputs = ({
  alarmData,
  alarmValues,
  setAlarmValues,
  setModalDisableSave,
  isAdmin,
}: StationNoDataAlarmInputsProps) => {
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const alarmId = alarmData?.id;

  // Initialise only when a different alarm is loaded: refetches (e.g. after adding a recipient) must not discard
  // unsaved changes
  React.useEffect(() => {
    // Only the fields the API accepts for station-level alarms
    setAlarmValues({
      no_data_minutes: alarmData?.no_data_minutes || DEFAULT_NO_DATA_MINUTES,
      alert_frequency_minutes: alarmData?.alert_frequency_minutes || 1440,
      level: alarmData?.level || "notice",
      active: alarmData?.active ?? true,
      internal: alarmData?.internal ?? false,
    });
    // Every field has a valid default, so a new alarm can be saved right away
    setModalDisableSave(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmId, setAlarmValues, setModalDisableSave]);

  const setValue = (key: string, value: any) => {
    setAlarmValues({
      ...(alarmValues || {}),
      [key]: value,
    });
  };

  return (
    <>
      <h4>{gettext("Alarm Configuration")}</h4>

      {alarmData?.triggered_at && (
        <div className="alert alert-warning">
          {gettext(
            "This alarm is currently triggered: the station has not sent any data within the selected period."
          )}
        </div>
      )}

      <Form.Group as={Row} className="mb-3">
        <FormLabel column sm={3}>
          {gettext("No data period")}{" "}
          <DefaultTooltip
            popHeader={gettext("No data period")}
            popBody={
              <>
                <p>
                  {gettext(
                    "The alarm is triggered when the station has not received any data for this period. Manually entered values also count as received data. Once the station sends data again, the recipients are notified that the station is sending data again."
                  )}
                </p>
              </>
            }
          />
        </FormLabel>
        <Col sm={9}>
          <Form.Select
            value={alarmValues?.no_data_minutes || DEFAULT_NO_DATA_MINUTES}
            onChange={(e) =>
              setValue("no_data_minutes", parseInt(e.target.value))
            }
          >
            {DEFAULT_NO_DATA_PERIODS.map(([label, minutes]) => (
              <option key={minutes} value={minutes}>
                {label}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-3">
        <FormLabel column sm={3}>
          {gettext("Active")}
        </FormLabel>
        <Col sm={9}>
          <Form.Check
            type="switch"
            id="custom-switch-no-data-active"
            label={
              alarmValues?.active ?? true
                ? gettext("Enabled")
                : gettext("Disabled")
            }
            checked={alarmValues?.active ?? true}
            onChange={(e) => setValue("active", e.target.checked)}
          />
        </Col>
      </Form.Group>

      <div className="mb-3">
        <Button
          variant="link"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="p-0"
        >
          {showAdvanced
            ? gettext("Hide Advanced Options")
            : gettext("Show Advanced Options")}
        </Button>
      </div>

      {showAdvanced && (
        <>
          <h5>{gettext("Advanced Options")}</h5>

          <AlarmLevelField
            value={alarmValues?.level}
            onChange={(value) => setValue("level", value)}
          />

          <AlertFrequencyField
            value={alarmValues?.alert_frequency_minutes}
            onChange={(e) =>
              setValue("alert_frequency_minutes", parseInt(e.target.value))
            }
            tooltip={gettext(
              "How often to repeat the alert while the station is not sending data"
            )}
          />

          {isAdmin && (
            <InternalField
              checked={!!alarmValues?.internal}
              onChange={(e) => setValue("internal", e.target.checked)}
            />
          )}
        </>
      )}
    </>
  );
};

interface AlarmRecipientsProps {
  alarmData: Pick<
    GetAlarmBySourceUUID_alert,
    "id" | "alert_recipients"
  > | null;
  // Sent with every recipient request so the API can check the alarm's organization
  requestParams: Record<string, any>;
  refetchAlarm: () => Promise<any>;
  saveAlarm: (shouldNavigateAway?: boolean) => Promise<number | null>;
  modalDisableSave: boolean;
  setModalPayload: (payload: any) => void;
}

const editRecipientActions = {
  save: "save",
  delete: "delete",
  cancel: "cancel",
  add: "add",
};

const AlarmRecipients = ({
  alarmData,
  requestParams,
  refetchAlarm,
  saveAlarm,
  modalDisableSave,
  setModalPayload,
}: AlarmRecipientsProps) => {
  const [editByRecipientId, setEditByRecipientId] = React.useState<
    number | null
  >(null);
  const [editRecipient, setEditRecipient] = React.useState<Record<string, any>>(
    {}
  );
  const [newRecipient, setNewRecipient] = React.useState<Record<string, any>>(
    {}
  );

  const setNewRecipientValues = (key: string) => (e: any) => {
    const value: string = e.target.value;
    setNewRecipient({
      ...newRecipient,
      [key]: value,
    });
  };

  const setEditRecipientValues = (key: string) => (e: any) => {
    const recipient = alarmData?.alert_recipients.find(
      (r) => r.id === editByRecipientId
    );
    if (!recipient) return;
    const value: string = e.target.value;
    setEditRecipient({
      ...editRecipient,
      [key]: value,
    });
  };

  const resetAddStates = () => {
    setNewRecipient({});
  };

  const enableEditRow = (id: number) => () => {
    const recipient = alarmData?.alert_recipients.find((r) => r.id === id);
    if (!recipient) return;
    setEditByRecipientId(id);
    setEditRecipient({
      email_recipient: recipient.email_recipient ?? "",
      sms_recipient: recipient.sms_recipient ?? "",
    });
  };

  const cancelEditRow = () => {
    setEditByRecipientId(null);
    setEditRecipient({});
  };

  const editActionHandler = React.useCallback(
    (action: string, recipientId: number | null = null) => {
      const alarmId = alarmData?.id || null;
      switch (action) {
        case editRecipientActions.save:
          if (!editByRecipientId) return;
          const editValues = { ...editRecipient };

          if (!editValues.email_recipient && !editValues.sms_recipient) {
            toast.error(
              <ToastMessage
                messageHeader="Error"
                message="Please provide at least one contact method"
              />
            );
            return;
          }

          apiClient
            .request("/organization-admin/alert-recipient/edit", {
              method: "POST",
              data: {
                alert_recipient_id: editByRecipientId,
                ...requestParams,
                ...editValues,
              },
            })
            .then(() => {
              const notificationMessage =
                getRecipientNotificationMessage(editValues);
              toast.success(
                <ToastMessage
                  messageHeader="Recipient updated successfully"
                  message={notificationMessage}
                />
              );
              refetchAlarm()
                .then(() => {
                  cancelEditRow();
                })
                .catch(console.error);
            })
            .catch((e) => {
              console.error(e);
              toast.error(
                <ToastMessage
                  messageHeader="Error updating recipient"
                  message={e.message.toString()}
                />
              );
            });
          break;

        case editRecipientActions.cancel:
          cancelEditRow();
          break;

        case editRecipientActions.delete:
          if (!recipientId) return;

          if (
            !window.confirm(
              gettext("Are you sure you want to delete this recipient?")
            )
          )
            return;

          apiClient
            .request("/organization-admin/alert-recipient/delete", {
              method: "POST",
              data: {
                alert_recipient_id: recipientId,
                ...requestParams,
                alert_id: alarmData?.id,
              },
            })
            .then(() => {
              toast.success(
                <ToastMessage
                  messageHeader="Recipient deleted successfully"
                  message=""
                />
              );
              refetchAlarm().catch(console.error);
            })
            .catch((e) => {
              console.error(e);
              toast.error(
                <ToastMessage
                  messageHeader="Error deleting recipient"
                  message={e.message.toString()}
                />
              );
            });
          break;

        case editRecipientActions.add:
          const newValues = { ...newRecipient };

          if (!newValues.email_recipient && !newValues.sms_recipient) {
            toast.error(
              <ToastMessage
                messageHeader="Error"
                message="Please provide at least one contact method"
              />
            );
            return;
          }

          // If we're adding a recipient but the alarm hasn't been created yet
          if (!alarmData || modalDisableSave) {
            // Check if at least one limit is set
            if (modalDisableSave) {
              toast.error(
                <ToastMessage
                  messageHeader="Please configure the alarm first"
                  message="You need to set at least one limit (upper or lower) before adding recipients"
                />
              );
              break;
            }

            // Save the alarm first WITHOUT navigating away, then add recipient
            saveAlarm(false)
              .then((alertId) => {
                if (!alertId) {
                  toast.error(
                    <ToastMessage
                      messageHeader="Error creating alarm"
                      message="Could not retrieve alert ID"
                    />
                  );
                  return;
                }

                // Refetch to get the newly created alarm and update modalPayload
                refetchAlarm()
                  .then((result) => {
                    // Find the newly created alarm in the query results
                    const alerts = result?.data?.alert || [];
                    const newAlarm = alerts.find(
                      (alarm: any) => alarm.id === alertId
                    );

                    if (newAlarm) {
                      // Update modalPayload so editingAlarm is set for future saves
                      setModalPayload(newAlarm);
                    }

                    // Now create the recipient with the new alert_id
                    apiClient
                      .request("/organization-admin/alert-recipient/create", {
                        method: "POST",
                        data: {
                          alert_id: alertId,
                          ...requestParams,
                          ...newValues,
                        },
                      })
                      .then(() => {
                        const notificationMessage =
                          getRecipientNotificationMessage(newValues);
                        toast.success(
                          <ToastMessage
                            messageHeader="Recipient added successfully"
                            message={notificationMessage}
                          />
                        );
                        refetchAlarm()
                          .then(() => {
                            resetAddStates();
                          })
                          .catch(console.error);
                      })
                      .catch((e) => {
                        console.error(e);
                        toast.error(
                          <ToastMessage
                            messageHeader="Error adding recipient"
                            message={e.message.toString()}
                          />
                        );
                      });
                  })
                  .catch(console.error);
              })
              .catch((e) => {
                console.error(e);
                // Error already shown by saveAlarm
              });
          } else {
            // Alarm already exists, add recipient directly
            apiClient
              .request("/organization-admin/alert-recipient/create", {
                method: "POST",
                data: {
                  alert_id: alarmData.id,
                  ...requestParams,
                  ...newValues,
                },
              })
              .then(() => {
                const notificationMessage =
                  getRecipientNotificationMessage(newValues);
                toast.success(
                  <ToastMessage
                    messageHeader="Recipient added successfully"
                    message={notificationMessage}
                  />
                );
                refetchAlarm()
                  .then(() => {
                    resetAddStates();
                  })
                  .catch(console.error);
              })
              .catch((e) => {
                console.error(e);
                toast.error(
                  <ToastMessage
                    messageHeader="Error adding recipient"
                    message={e.message.toString()}
                  />
                );
              });
          }
          break;

        default:
          break;
      }
    },
    [
      editByRecipientId,
      editRecipient,
      alarmData,
      requestParams,
      refetchAlarm,
      saveAlarm,
      modalDisableSave,
      newRecipient,
      setModalPayload,
    ]
  );

  return (
    <>
      <h4>{gettext("Recipients")}</h4>
      <Table responsive>
        <thead>
          <tr>
            <th>{gettext("Email")}</th>
            <th>{gettext("SMS")}</th>
            <th>{gettext("Actions")}</th>
          </tr>
        </thead>
        <tbody>
          {alarmData?.alert_recipients.map((recipient) => {
            const isEditing = editByRecipientId === recipient.id;
            return (
              <tr key={`recipient-${recipient.id}`}>
                <td>
                  {isEditing ? (
                    <FormControl
                      type="email"
                      defaultValue={recipient.email_recipient ?? ""}
                      onChange={setEditRecipientValues("email_recipient")}
                    />
                  ) : (
                    recipient.email_recipient
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <PhoneNumberFormControl
                      defaultValue={recipient.sms_recipient ?? ""}
                      onChange={setEditRecipientValues("sms_recipient")}
                    />
                  ) : (
                    recipient.sms_recipient
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <>
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() =>
                          editActionHandler(editRecipientActions.save)
                        }
                        className="me-2"
                      >
                        {gettext("Save")}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          editActionHandler(editRecipientActions.cancel)
                        }
                      >
                        {gettext("Cancel")}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={enableEditRow(recipient.id)}
                        className="me-2"
                      >
                        {gettext("Edit")}
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() =>
                          editActionHandler(
                            editRecipientActions.delete,
                            recipient.id
                          )
                        }
                      >
                        {gettext("Delete")}
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
          <tr>
            <td>
              <FormControl
                type="email"
                placeholder={gettext("Add e-mail address here")}
                value={newRecipient.email_recipient || ""}
                onChange={setNewRecipientValues("email_recipient")}
              />
            </td>
            <td>
              <PhoneNumberFormControl
                placeholder={gettext("Enter phone number")}
                value={newRecipient.sms_recipient || ""}
                onChange={setNewRecipientValues("sms_recipient")}
              />
            </td>
            <td>
              <Button
                size="sm"
                variant="success"
                onClick={() => editActionHandler(editRecipientActions.add)}
                disabled={
                  !newRecipient.email_recipient && !newRecipient.sms_recipient
                }
              >
                {gettext("Add")}
              </Button>
            </td>
          </tr>
        </tbody>
      </Table>
    </>
  );
};

export default AddNewAlarmForm;
